import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CountBadge } from "@/components/shared/CountBadge";

interface Brand {
  id: number | string;
  name: string;
}

interface Preset {
  id: string;
  name: string;
  brandIds: string[];
  isDefault?: boolean;
}

interface PresetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingPreset: { id: string; name: string; brandIds: string[] } | null;
  presetName: string;
  onPresetNameChange: (name: string) => void;
  presetBrandIds: string[];
  onToggleBrand: (brandId: string) => void;
  allBrands: Brand[];
  brandGroupPresets: Preset[];
  onSave: () => void;
  onSetPresetName: (name: string) => void;
  onSetPresetBrandIds: (ids: string[]) => void;
  presetToDelete: { id: string; name: string } | null;
  onPresetToDeleteChange: (preset: { id: string; name: string } | null) => void;
  onConfirmDeletePreset: () => void;
}

const defaultPresetsData: Record<string, { name: string; brandIds: string[] }> = {
  "preset-top": { name: "Top Tier Partners", brandIds: ["supergoop"] },
  "preset-mid": { name: "Mid Tier Partners", brandIds: ["glossier", "coola"] },
  "preset-low": { name: "Low Tier Partners", brandIds: ["newbrand", "thelabco", "kinfield"] },
};

export function PresetDialog({
  open,
  onOpenChange,
  editingPreset,
  presetName,
  onPresetNameChange,
  presetBrandIds,
  onToggleBrand,
  allBrands,
  brandGroupPresets,
  onSave,
  onSetPresetName,
  onSetPresetBrandIds,
  presetToDelete,
  onPresetToDeleteChange,
  onConfirmDeletePreset,
}: PresetDialogProps) {
  const isDefaultPreset = editingPreset
    ? brandGroupPresets.find((p) => p.id === editingPreset.id)?.isDefault
    : false;

  const handleResetToDefault = () => {
    if (!editingPreset) return;
    const def = defaultPresetsData[editingPreset.id];
    if (def) {
      onSetPresetName(def.name);
      onSetPresetBrandIds([...def.brandIds]);
      toast.success("Reset to Default", { description: `"${def.name}" values restored.` });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[420px] bg-card">
          <DialogHeader>
            <DialogTitle>{editingPreset ? "Edit Preset" : "Create Preset"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Preset Name</Label>
              <Input
                placeholder="e.g. Top Tier Partners"
                value={presetName}
                onChange={(e) => onPresetNameChange(e.target.value)}
                className="h-9 rounded-lg bg-card"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs inline-flex items-center gap-1.5">
                Brands <CountBadge size="sm" count={presetBrandIds.length} /> selected
              </Label>
              <div className="border border-border rounded-lg max-h-[240px] overflow-y-auto divide-y divide-border">
                {allBrands.map((brand) => {
                  const isSelected = presetBrandIds.includes(brand.id.toString());
                  return (
                    <button
                      key={brand.id}
                      onClick={() => onToggleBrand(brand.id.toString())}
                      className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-muted/30 text-sm text-foreground transition-colors text-left"
                    >
                      <div className={`h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${isSelected ? "bg-primary border-primary" : "border-border"}`}>
                        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      {brand.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {isDefaultPreset && (
              <Button
                variant="outline"
                className="rounded-lg mr-auto text-muted-foreground"
                onClick={handleResetToDefault}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Reset to Default
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={onSave} disabled={!presetName.trim()} className="rounded-lg">
              {editingPreset ? "Save Changes" : "Create Preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
